import { useEffect, useRef } from "react";

import { useMergeRefs } from "@floating-ui/react";

import type { SuggestionsModel } from "@/models/SuggestionsModel";
import type { Ref } from "react";

/**
 * Keeps the active suggestion visible inside its scrollable list. When an option
 * becomes active via keyboard navigation it is scrolled into view; pointer-hover
 * activations are ignored so the list never scrolls out from under the cursor.
 *
 * Returns a ref merged with `forwardedRef` to attach to the option element.
 */
export function useScrollActiveIntoView<T extends HTMLElement>(
  isActive: boolean,
  suggestions: SuggestionsModel,
  forwardedRef?: Ref<T>,
) {
  const ref = useRef<T>(null);

  useEffect(() => {
    if (isActive && suggestions.activeIndexFromKeyboard) {
      ref.current?.scrollIntoView({ block: "nearest" });
    }
  }, [isActive, suggestions]);

  return useMergeRefs([ref, forwardedRef]);
}
